import React, { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const terms = [
  { k: "Instrument", v: "Guarantee Convertible Bond" },
  { k: "ISIN", v: "SE00·PENDING" },
  { k: "Coupon", v: "7.5% p.a." },
  { k: "Tenor", v: "36 months" },
  { k: "Conversion", v: "IP equity, capped" },
  { k: "Guarantee", v: "First-loss reserve · 15%" },
  { k: "Currency", v: "EUR / SEK / USD" },
  { k: "Minimum", v: "€50,000" },
];

export const BondSection = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    amount: "",
    country: "",
    note: "",
    accredited: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.amount) {
      toast.error("Name, email and amount are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/investors`, form);
      setSubmitted(true);
      toast.success("Interest registered. Erik will be in touch.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Submission failed. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="bond"
      className="relative py-28 md:py-40 bg-ink-950 grid-bg"
      data-testid="bond-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="flex items-baseline justify-between border-b border-white/10 pb-6 mb-16">
          <div className="font-mono text-[11px] tracking-[0.35em] uppercase text-spectrum-green">
            ◢ Bond Prospectus
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
            Series 01 · 2025
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: terminal-style term sheet */}
          <div className="lg:col-span-6">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[0.95] mb-8"
              data-testid="bond-title"
            >
              Underwrite the <span className="spectrum-text">automated</span> future.
            </motion.h2>
            <p className="text-white/60 text-base md:text-lg font-light max-w-lg mb-10 leading-relaxed">
              A guarantee-backed convertible bond issued by Svenska Invest / EA to
              finance the development of the Merkle-ledger commerce stack. Fixed
              coupon downside. Software-IP upside on conversion.
            </p>

            <div className="border border-white/10 bg-ink-900 relative overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-white/[0.02]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-spectrum-green" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                    term-sheet.svi
                  </span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                  READ-ONLY
                </div>
              </div>
              <dl className="divide-y divide-white/10">
                {terms.map((t) => (
                  <div key={t.k} className="grid grid-cols-2 px-5 py-4">
                    <dt className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">
                      {t.k}
                    </dt>
                    <dd className="font-mono text-sm text-white text-right">{t.v}</dd>
                  </div>
                ))}
              </dl>
              <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                  Full prospectus on request
                </div>
                <a
                  href="mailto:erik@svenskainvest.com?subject=Bond%20Prospectus%20Request"
                  className="font-mono text-[11px] uppercase tracking-[0.25em] text-spectrum-green hover:underline"
                  data-testid="bond-prospectus-mailto"
                >
                  request →
                </a>
              </div>
            </div>
          </div>

          {/* Right: Investor form */}
          <div className="lg:col-span-6">
            <div className="border border-white/10 bg-ink-900 p-8 md:p-10 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-spectrum-violet/30 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-spectrum-green mb-3">
                  ◢ Register interest
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight mb-8">
                  Investor intake — Series 01
                </h3>

                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="py-8"
                    data-testid="investor-success"
                  >
                    <div className="font-mono text-xs uppercase tracking-[0.3em] text-spectrum-green mb-4">
                      ◈ Interest received
                    </div>
                    <p className="text-white text-lg font-light mb-6">
                      Thank you, {form.name.split(" ")[0] || "friend"}. Erik will reach out from{" "}
                      <span className="text-spectrum-green">erik@svenskainvest.com</span> with the
                      full prospectus.
                    </p>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setForm({ name: "", email: "", amount: "", country: "", note: "", accredited: false });
                      }}
                      className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white"
                      data-testid="investor-reset"
                    >
                      ← submit another
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="investor-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <Field label="Name">
                        <Input
                          value={form.name}
                          onChange={onChange("name")}
                          placeholder="Full name"
                          className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white h-11"
                          data-testid="investor-name"
                        />
                      </Field>
                      <Field label="Email">
                        <Input
                          value={form.email}
                          onChange={onChange("email")}
                          type="email"
                          placeholder="you@fund.com"
                          className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white h-11"
                          data-testid="investor-email"
                        />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <Field label="Amount (EUR)">
                        <Input
                          value={form.amount}
                          onChange={onChange("amount")}
                          placeholder="€ 250,000"
                          className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white h-11"
                          data-testid="investor-amount"
                        />
                      </Field>
                      <Field label="Jurisdiction">
                        <Input
                          value={form.country}
                          onChange={onChange("country")}
                          placeholder="Sweden"
                          className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white h-11"
                          data-testid="investor-country"
                        />
                      </Field>
                    </div>
                    <Field label="Note (optional)">
                      <Textarea
                        value={form.note}
                        onChange={onChange("note")}
                        placeholder="Fund thesis, ticket size, timing..."
                        rows={3}
                        className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white resize-none"
                        data-testid="investor-note"
                      />
                    </Field>

                    <label className="flex items-start gap-3 pt-2" data-testid="investor-accredited-label">
                      <Checkbox
                        checked={form.accredited}
                        onCheckedChange={(v) => setForm({ ...form, accredited: !!v })}
                        className="mt-0.5 border-white/30 data-[state=checked]:bg-spectrum-green data-[state=checked]:text-ink-950 rounded-none"
                        data-testid="investor-accredited"
                      />
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 leading-relaxed">
                        I qualify as an accredited / professional investor in my jurisdiction.
                      </span>
                    </label>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 rounded-none bg-spectrum-green text-ink-950 hover:bg-white font-mono text-[11px] uppercase tracking-[0.3em] disabled:opacity-60"
                      data-testid="investor-submit"
                    >
                      {loading ? "Transmitting..." : "Submit interest →"}
                    </Button>

                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 pt-1 leading-relaxed">
                      Not a public offer. Follow-up handled personally by Erik at
                      erik@svenskainvest.com.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
      {label}
    </div>
    {children}
  </label>
);
